import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useContext, useMemo } from 'react';
import styled from 'styled-components';
import { useQueryClient } from '@tanstack/react-query';
import { useAlternatePaymentMethodsDeletion } from '@syf-api-library/alternate-payment-methods';
import { autopayStatusCacheKey, queryFnFactory, useAutopayStatusMutation, useIsAutopayStatusMutating } from '@syf-api-library/autopay-status';
import SlideInModalLoader from 'syf-component-library/ui/atoms/Loader/Skeleton/SlideInModalLoader';
import { ModalFooter } from 'syf-component-library/ui/patterns/SlideInModal';
import { FooterContainer } from 'syf-component-library/ui/patterns/SlideInModal/subcomponents';
import { Inset } from 'syf-component-library/ui/spacing';
import { DATA_OBJECT, PAGE_FUNCTION, PAGE_NAME, SUB_FUNCTION } from '../../../../const/analytics';
import { VALIDATION_ERROR_CODE } from '../../../../const/errorCode';
import { paymentOptions, paymentTypes } from '../../../../const/paymentOptions';
import MainContext from '../../../../Context';
import { colors } from '../../../../defaultTheme';
import { generateAutopayPayload, getAutopayIntroHeading, getIsBankrupt, getPaymentAmountFromOption, isAutopayEnrollmentEligible, isPayLaterProduct, isPaymentMethodErrorCode, isPN4, isSecuredInstallmentLoanProduct, isWaivedInterestAvailableForAutopay } from '../../../../helpers';
import useApiMachine from '../../../../hooks/machines/useApiMachine';
import useFormValues from '../../../../hooks/machines/useFormValues';
import usePaymentFlowMachine from '../../../../hooks/machines/usePaymentFlowMachine';
import { useApiConfig } from '../../../../hooks/useApiConfig';
import { t } from '../../../../strings/i18n';
import { useXForm, XForm } from '../../../../xform';
import AutopayLegal from '../../../atoms/AutopayLegal';
import { AutopaySchedule } from '../../../atoms/AutopaySchedule';
import IntroHeading from '../../../atoms/IntroHeading';
import { PaymentModalWrapper } from '../../../atoms/styled';
import AutopayEnrollmentMessageBox from '../../MessageBox/AutopayEnrollmentMessageBox';
import AutopayUnavailableMessage from '../../MessageBox/AutopayUnavailableMessage';
import { AutopayAmountSelect, PaymentMethodSelect, PaymentTypeSelect } from '../PaymentFormSubcomponents';
const Form = ({ isAutopayEnrolled, isPaymentTypeVisible }) => {
    const { submit } = useXForm();
    const handleSubmit = (e) => {
        e.preventDefault();
        submit();
    };
    return (_jsxs("form", Object.assign({ onSubmit: handleSubmit }, { children: [!isAutopayEnrolled && isPaymentTypeVisible && (_jsx(PaymentTypeSelect, { value: paymentTypes.AUTOPAY })), _jsx(AutopayAmountSelect, { id: "amount" }), _jsx(PaymentMethodSelect, { id: "paymentMethod" }), _jsx("input", { "aria-hidden": true, "data-testid": "submit-button", style: { display: 'none' }, type: "submit" })] })));
};
const NextDueDateBorderBox = styled(Inset) `
  border-top: 1px solid ${colors.mediumGrey};
  border-bottom: 1px solid ${colors.mediumGrey};
`;
const NextDueDateMissingMessage = () => {
    return (_jsx(Inset, Object.assign({ bottom: "large" }, { children: _jsx(NextDueDateBorderBox, Object.assign({ horizontal: "base", vertical: "xlarge" }, { children: t('autopay.noNextDueDate') })) })));
};
function getNextDueDate(autopayStatus, paymentEnterpriseEligibility) {
    var _a;
    if (autopayStatus.enrollment) {
        return autopayStatus.enrollmentDetails.nextDueDate;
    }
    const autopayEnrollmentEligibility = paymentEnterpriseEligibility.AUTOPAY.find((eligibility) => eligibility.subEligibilityType === 'AUTOPAY_ENROLL');
    const { value: effectiveDueDate } = ((_a = autopayEnrollmentEligibility.criteria) === null || _a === void 0 ? void 0 : _a.find(({ attribute }) => attribute === 'effectiveDueDate')) || {};
    return effectiveDueDate;
}
const AutopayForm = () => {
    const { triggerAnalyticsEvent, sendToMainMachine } = useContext(MainContext);
    const [, autopayActor, sendToAutopay] = usePaymentFlowMachine();
    const { form: formMachine, isPaymentTypeVisible } = autopayActor.context;
    const queryClient = useQueryClient();
    const formValues = useFormValues();
    const apiConfig = useApiConfig();
    const [apiMachine] = useApiMachine();
    const { accountSummaryQueryResult, alternatePaymentMethodsQueryResult, autopayStatusQueryResult, paymentEnterpriseEligibilityQueryResult } = apiMachine.context;
    const { data: accountSummary } = accountSummaryQueryResult;
    const { data: autopayStatus } = autopayStatusQueryResult;
    const { data: alternatePaymentMethodsData } = alternatePaymentMethodsQueryResult;
    const { data: paymentEnterpriseEligibility } = paymentEnterpriseEligibilityQueryResult;
    const deleteAlternatePaymentMethod = useAlternatePaymentMethodsDeletion(apiConfig);
    const autopayStatusMutation = useAutopayStatusMutation(apiConfig);
    const isAutopayStatusMutating = useIsAutopayStatusMutating(apiConfig);
    const { conditions, product } = accountSummary || {};
    const isAutopayEnrolled = autopayStatus.enrollment;
    const nextDueDate = getNextDueDate(autopayStatus, paymentEnterpriseEligibility);
    const isBankrupt = getIsBankrupt(conditions);
    const isEnrolling = useMemo(() => !isAutopayEnrolled, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
    const isEnrollmentEligible = isAutopayEnrollmentEligible(paymentEnterpriseEligibility);
    const isWaivedInterestAvailable = isWaivedInterestAvailableForAutopay(paymentEnterpriseEligibility);
    const shouldAutopayUnavailable = isBankrupt || (isEnrolling && !isEnrollmentEligible);
    const { type: productType } = accountSummary.product;
    const fetchAutopayStatus = queryFnFactory(apiConfig.config, apiConfig.keyProps);
    const isSelectAutopayPaymentMethodMessage = Boolean(isSecuredInstallmentLoanProduct(productType) ||
        (isPayLaterProduct(productType) && !isWaivedInterestAvailable));
    const submit = async () => {
        const { paymentOption, paymentMethod: paymentMethodId } = formValues;
        const paymentAmount = paymentOption === paymentOptions.OTHER_PAYMENT
            ? formValues.paymentAmount
            : getPaymentAmountFromOption(paymentOption, apiMachine.context);
        const data = generateAutopayPayload({
            paymentOption,
            paymentAmount,
            paymentMethod: paymentMethodId,
            accountSummary
        });
        const response = await autopayStatusMutation.mutateAsync(data);
        if (response === null) {
            const autopayStatusPayload = await fetchAutopayStatus();
            const cacheKey = autopayStatusCacheKey(apiConfig.keyProps);
            // Set React Query cache data
            queryClient.setQueryData(cacheKey, autopayStatusPayload);
        }
    };
    const onSubmitHandler = () => {
        var _a;
        // Delete Alternate Payment Method
        const alternatePaymentMethod = (_a = alternatePaymentMethodsData === null || alternatePaymentMethodsData === void 0 ? void 0 : alternatePaymentMethodsData.paymentMethods) === null || _a === void 0 ? void 0 : _a.find((paymentMethod) => paymentMethod.id === formValues.paymentMethod);
        if (alternatePaymentMethod) {
            deleteAlternatePaymentMethod.mutateAsync(alternatePaymentMethod);
        }
        switch (true) {
            case !isEnrolling:
                sendToAutopay({ type: 'SUCCESS', successType: 'updating' });
                break;
            case isEnrolling:
                sendToAutopay({ type: 'SUCCESS', successType: 'enrolling' });
                break;
            default:
                break;
        }
    };
    const onSubmitErrorHandler = async (error) => {
        var _a, _b;
        // TODO: This anlaytic event should be scoped in `autopayMachine` once machines are able to read status codes
        triggerAnalyticsEvent({
            pageFunction: PAGE_FUNCTION.MAKE_PAYMENT.AUTOPAY,
            pageSubFunction: isEnrolling
                ? SUB_FUNCTION.AUTOPAY.ENROLL
                : SUB_FUNCTION.AUTOPAY.TURN_OFF_AUTOPAY,
            pageName: `${PAGE_NAME.AUTOPAY.FAILED}:${(_a = error.response) === null || _a === void 0 ? void 0 : _a.status}`
        });
        if (((_b = error.response) === null || _b === void 0 ? void 0 : _b.status) === VALIDATION_ERROR_CODE) {
            const errorBody = await error.response.json();
            const [businessError] = (errorBody === null || errorBody === void 0 ? void 0 : errorBody.businessErrors) || [];
            const isErrorRelatedToPaymentMethod = isPaymentMethodErrorCode(businessError === null || businessError === void 0 ? void 0 : businessError.code);
            if (isErrorRelatedToPaymentMethod) {
                deleteAlternatePaymentMethod.mutateAsync({
                    id: formValues.paymentMethod
                });
                if (isAutopayEnrolled) {
                    sendToAutopay({
                        type: 'ERROR',
                        errorType: 'updating',
                        hasPaymentMethodBusinessValidationError: isErrorRelatedToPaymentMethod
                    });
                    return;
                }
                if (!isAutopayEnrolled) {
                    sendToAutopay({
                        type: 'ERROR',
                        errorType: 'enrolling',
                        hasPaymentMethodBusinessValidationError: isErrorRelatedToPaymentMethod
                    });
                }
                return;
            }
        }
        if (isAutopayEnrolled) {
            sendToAutopay({
                type: 'ERROR',
                errorType: 'updating'
            });
            return;
        }
        if (!isAutopayEnrolled) {
            sendToAutopay({
                type: 'ERROR',
                errorType: 'enrolling'
            });
        }
    };
    const handleCancel = () => {
        // This can be true if user is cancelling autopay
        if (isAutopayStatusMutating) {
            return;
        }
        if (isAutopayEnrolled) {
            sendToMainMachine({
                type: 'SET_ONE_TIME_PAYMENT_FLOW'
            });
        }
        else {
            sendToAutopay({ type: 'CANCEL' });
        }
    };
    /*
     * NOTE: This conditional should not consider the global `isAutopayStatusMutating` boolean.
     * This will have the effect of unmounting `<AutopayBanner />`, which is what is currently
     * performing the cancel autopay mutation and will thus be unable to redirect
     * the user to the cancellation confirmation page
     */
    if (autopayStatusMutation.isLoading) {
        return _jsx(SlideInModalLoader, {});
    }
    return (_jsxs(_Fragment, { children: [_jsx(AutopayEnrollmentMessageBox, { isEnrolling: isEnrolling, isEnrollmentEligible: !shouldAutopayUnavailable }), _jsx(Inset, Object.assign({ bottom: "small", top: "large", vertical: "large" }, { children: _jsx(IntroHeading, { children: getAutopayIntroHeading(isSelectAutopayPaymentMethodMessage) }) })), _jsx(PaymentModalWrapper, Object.assign({ bottom: "xlarge", vertical: "large" }, { children: shouldAutopayUnavailable ? (_jsx(AutopayUnavailableMessage, { isBankrupt: isBankrupt })) : (_jsxs(_Fragment, { children: [_jsx(XForm, Object.assign({ formMachine: formMachine, submit: submit, onSubmit: onSubmitHandler, onSubmitError: onSubmitErrorHandler }, { children: _jsx(Form, { isAutopayEnrolled: isAutopayEnrolled, isPaymentTypeVisible: isPaymentTypeVisible }) })), nextDueDate ? (_jsx(AutopaySchedule, { nextDueDate: nextDueDate, product: product, shouldShowAutopayDate: !isPN4(productType), shouldShowNextPaymentDate: !isPayLaterProduct(productType) })) : (_jsx(NextDueDateMissingMessage, {})), _jsx(AutopayLegal, { heading: t('legal.termsAndConditions'), product: product })] })) })), _jsx(FooterContainer, { children: _jsx(ModalFooter, { dataCy: "autopay-modal-footer", dataPrimaryObject: DATA_OBJECT.BUTTON, dataPrimaryReason: isAutopayEnrolled ? 'save changes' : 'enroll in autopay', dataSecondaryObject: DATA_OBJECT.BUTTON, dataSecondaryReason: "cancel", dataType: "make a payment", handlePrimaryButton: () => formMachine.send({
                        type: 'SUBMIT'
                    }), handleSecondaryButton: handleCancel, isPrimaryDisabled: shouldAutopayUnavailable, primaryText: isAutopayEnrolled
                        ? t('button.saveChanges')
                        : t('button.enrollInAutopay'), secondaryButtonType: "ghost", secondaryText: t('button.cancel') }) })] }));
};
export default AutopayForm;
